import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { subMonths } from 'date-fns';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { IconFileExport, IconSearch } from '@tabler/icons-react';
import { Button, DateTimePicker, IconButton, Select } from '@atoms';
import { useDownloadPartnerTransactions } from '@hooks';
import { isoStringToDate } from '@utils';
import { DATE_PICKERS, DATE_PICKER_PROPS, FILTER_FIELDS, validationSchema } from './constants';
import {
  EVENT_MY_BALANCE_CLEAR_FILTER_MOVEMENTS,
  EVENT_MY_BALANCE_EXPORT_MOVEMENTS,
  myBalanceClick,
  myBalanceFilterMovements,
} from './events';
import styles from './styles';

/**
 * Displays filters
 * @param {object} props
 * @param {object} props.params
 */
const SearchBoxClientsMoves = ({
  isLoading,
  params,
  onSubmit,
  disabledDownload = false,
  idArea,
}) => {
  const { t } = useTranslation();
  const responsive = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const now = new Date();
  const monthAgo = subMonths(now, 1);

  const fromDate = params?.fromDate ? isoStringToDate(params?.fromDate) : monthAgo;
  const toDate = params?.toDate ? isoStringToDate(params?.toDate) : now;

  const initialValues = {
    fromDate,
    toDate,
    customerTransactionType: params?.customerTransactionType || '',
  };

  const { handleChange, setFieldValue, handleSubmit, isValid, errors, values, resetForm } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit,
    });

  const { downloadPartnerTransactions, isLoading: isLoadingDownloadPartnerTransactions } =
    useDownloadPartnerTransactions();

  /**
   * Clears the filters
   */
  function handleClearFilters() {
    myBalanceClick(EVENT_MY_BALANCE_CLEAR_FILTER_MOVEMENTS);
    resetForm();
  }

  /**
   * change date field
   */
  function handleDateChange(fieldName, date) {
    setFieldValue(fieldName, date);
  }

  const disableButton = isLoading || !isValid;

  useEffect(() => {
    resetForm();
  }, [idArea, resetForm]);

  return (
    <Box sx={styles.searchContainer}>
      <Box sx={styles.header}>
        <Typography variant='h4'>{t('MOVEMENTS:FILTERS_TITLE')}</Typography>
      </Box>
      <Box style={styles.content(responsive)}>
        <Box style={styles.firstLine(responsive)}>
          <Box style={styles.inputsContainer(responsive)}>
            {DATE_PICKERS.map(({ name, label }) => (
              <DateTimePicker
                key={name}
                name={name}
                value={values[name]}
                handleChange={(date) => handleDateChange(name, date)}
                error={errors[name]}
                inputLabel={label}
                {...DATE_PICKER_PROPS}
              />
            ))}
            {FILTER_FIELDS(Boolean(idArea)).map(({ name, label, items, parseOption }) => (
              <Select
                key={name}
                onChange={handleChange(name)}
                value={values[name]}
                error={errors[name]}
                {...{ name, label, items, parseOption }}
              />
            ))}
          </Box>
        </Box>
        <Box style={styles.actionsContainer(responsive)}>
          <Button
            customStyle={styles.clearButton}
            text={t('COMMON:CLEAR_FILTER')}
            color='primary'
            variant='link'
            onClick={handleClearFilters}
          />

          <IconButton
            aria-label={t('COMMON:EXPORT')}
            tooltip='COMMON:EXPORT'
            IconComponent={IconFileExport}
            isLoading={isLoadingDownloadPartnerTransactions}
            disabled={isLoading || isLoadingDownloadPartnerTransactions || disabledDownload}
            onClick={() => {
              myBalanceClick(EVENT_MY_BALANCE_EXPORT_MOVEMENTS);
              downloadPartnerTransactions({ ...params, idArea });
            }}
          />

          <IconButton
            aria-label={t('COMMON:SEARCH')}
            tooltip='COMMON:SEARCH'
            IconComponent={IconSearch}
            variant='contained'
            isLoading={isLoading}
            disabled={disableButton}
            onClick={() => {
              myBalanceFilterMovements(values);
              handleSubmit();
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

SearchBoxClientsMoves.propTypes = {
  filtersOpen: PropTypes.bool,
  setFiltersOpen: PropTypes.func,
  isLoading: PropTypes.bool,
  params: PropTypes.object,
  onSubmit: PropTypes.func,
  showButtonDownload: PropTypes.bool,
  disabledDownload: PropTypes.bool,
  idArea: PropTypes.number,
};

export default SearchBoxClientsMoves;
